import React from 'react';
import { getAgeGroup } from '../../../../utils/pediatricAgeGroup';
import { developmentalMilestones, type Milestone } from '../../../../data/pediatric';

interface Props {
  milestones: Array<{ area: string; milestone: string; achieved: boolean }>;
  milestones_observations: string;
  setPediatricField: (section: string, field: string, value: any) => void;
  patientBirthDate: string | null;
}

const AREA_LABELS: Record<string, string> = {
  motor_gross: 'Motricidad Gruesa',
  motor_fine: 'Motricidad Fina',
  language: 'Lenguaje',
  social: 'Social',
};

const AREA_ICONS: Record<string, string> = {
  motor_gross: 'fa-arrows',
  motor_fine: 'fa-hand-peace',
  language: 'fa-comment',
  social: 'fa-users',
};

const DevelopmentMilestones: React.FC<Props> = ({
  milestones,
  milestones_observations,
  setPediatricField,
  patientBirthDate,
}) => {
  if (!patientBirthDate) {
    return (
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Hitos del Desarrollo</h5>
          <p className="text-muted small mt-2 mb-0">
            <i className="fa fa-info-circle me-1"></i>
            Seleccione un paciente con fecha de nacimiento para ver los hitos correspondientes.
          </p>
        </div>
      </div>
    );
  }

  const ageGroup = getAgeGroup(patientBirthDate);
  const filtered = developmentalMilestones.filter((m: Milestone) => m.age_group === ageGroup);
  const areas = [...new Set(filtered.map((m: Milestone) => m.area))];

  const milestoneMap = new Map<string, boolean>();
  milestones.forEach((m) => {
    milestoneMap.set(`${m.area}|${m.milestone}`, m.achieved);
  });

  const total = filtered.length;
  const achieved = filtered.filter((m: Milestone) =>
    milestoneMap.get(`${m.area}|${m.milestone}`)
  ).length;

  const handleToggle = (area: string, milestone: string, checked: boolean) => {
    const existing = [...milestones];
    const idx = existing.findIndex(
      (m) => m.area === area && m.milestone === milestone
    );
    if (idx >= 0) {
      existing[idx] = { ...existing[idx], achieved: checked };
    } else {
      existing.push({ area, milestone, achieved: checked });
    }
    setPediatricField('milestones', '', existing);
  };

  const percentage = total > 0 ? Math.round((achieved / total) * 100) : 0;

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="card-title mb-0">Hitos del Desarrollo</h5>
          <span className="badge bg-info">
            {ageGroup} · {achieved}/{total} hitos
          </span>
        </div>

        {total > 0 && (
          <div className="progress mb-3" style={{ height: 8 }}>
            <div
              className="progress-bar"
              role="progressbar"
              style={{
                width: `${percentage}%`,
                backgroundColor: percentage === 100 ? '#10b981' : percentage >= 50 ? '#f59e0b' : '#ef4444',
              }}
              aria-valuenow={percentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        )}

        {areas.map((area) => {
          const areaMilestones = filtered.filter((m: Milestone) => m.area === area);
          return (
            <div key={area} className="mb-3">
              <h6 className="text-muted fw-semibold">
                <i className={`fa ${AREA_ICONS[area] || 'fa-circle-check'} me-1`}></i>
                {AREA_LABELS[area] || area}
              </h6>
              <div className="row">
                {areaMilestones.map((m: Milestone) => {
                  const key = `${m.area}|${m.milestone}`;
                  const checked = milestoneMap.get(key) ?? false;
                  return (
                    <div className="col-md-4 mb-1" key={key}>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`ms-${key.replace(/\s+/g, '-')}`}
                          checked={checked}
                          onChange={(e) => handleToggle(m.area, m.milestone, e.target.checked)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`ms-${key.replace(/\s+/g, '-')}`}
                        >
                          {m.milestone}
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        <div className="row mt-2">
          <div className="col-md-8">
            <div className="mb-3">
              <label className="form-label">Observaciones del Desarrollo</label>
              <textarea
                className="form-control"
                rows={2}
                value={milestones_observations}
                onChange={(e) => setPediatricField('milestones_observations', '', e.target.value)}
                placeholder="Notas adicionales sobre el desarrollo psicomotor..."
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevelopmentMilestones;
